import React, {
  memo,
  useRef,
  useState,
  useCallback,
  useEffect,
  useMemo,
} from 'react';
import cn from 'classnames';

import { Icon } from 'components';
import { Radio, Input } from 'components/form';
import { useOnClickOutside } from 'hooks/useOnClickOutside';
import { Option } from 'lib/models/option';

import styles from './SingleSelect.module.scss';

export interface SingleSelectProps {
  /** Custom class name */
  className?: string;
  /** Custom style */
  style?: Record<string, unknown>;
  /** title */
  title: string;
  /** values */
  value: string;
  /** options */
  options: Array<Option>;
  /** onChange handler */
  onChange: (value: string) => void;
  /** alignment */
  alignment?: 'left' | 'center' | 'right';
  /** is it searchable? */
  isSearchable?: boolean;
  /** placeholder text for search */
  placeholder?: string;
  /** custom button class */
  triggerButtonClassName?: string;
  /** dropdown class name */
  dropdownClassName?: string;
  /** close dropdown on select */
  closeOnSelect?: boolean;
  /** direction of dropdown */
  direction?: 'top' | 'bottom';
  /** disabled */
  disabled?: boolean;
  /** hide chevron icon on disabled */
  hideChevronIconOnDisabled?: boolean;
}

export const SingleSelect: React.FC<SingleSelectProps> = memo(
  ({
    className = '', // custom class name
    style, // custom style
    options,
    title,
    value,
    onChange,
    isSearchable = true,
    placeholder,
    alignment = 'left',
    triggerButtonClassName,
    dropdownClassName,
    closeOnSelect = true,
    direction = 'bottom',
    disabled,
    hideChevronIconOnDisabled,
  }: SingleSelectProps) => {
    const contentClassNames = cn(styles.SingleSelect, className);
    const singleSelectRef = useRef(null);
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const trimmedSearch = search.trim();

    const onClose = useCallback(() => setOpen(false), []);

    useOnClickOutside({ ref: singleSelectRef, onOutsideClick: onClose });

    useEffect(() => {
      if (open === false) {
        setSearch('');
      }
    }, [open]);

    const onSelect = (value: string) => {
      onChange(value);
      if (closeOnSelect) onClose();
    };

    const filteredOptions = useMemo(() => {
      return isSearchable
        ? options.filter(
            (option) =>
              option.label.toLowerCase().indexOf(trimmedSearch.toLowerCase()) >
              -1
          )
        : options;
    }, [trimmedSearch, options, isSearchable]);

    const selectedValueLabel = useMemo(() => {
      return options.find((option) => option.value === value)?.label;
    }, [options, value]);

    return (
      <div
        className={contentClassNames}
        style={style}
        data-testid="SingleSelect"
        ref={singleSelectRef}
      >
        <button
          className={cn(
            styles.SingleSelectTrigger,
            {
              [styles.SingleSelectTriggerOpen]: open,
            },
            triggerButtonClassName
          )}
          type="button"
          onClick={() => setOpen(!open)}
          disabled={disabled}
        >
          {selectedValueLabel || title}
          {!(disabled && hideChevronIconOnDisabled) && (
            <Icon
              iconName="chevron-down"
              className={cn(styles.SingleSelectIcon, {
                [styles.SingleSelectIconReverse]: open,
              })}
            />
          )}
        </button>
        {open ? (
          <div
            className={cn(styles.SingleSelectDropdown, {
              [styles.SingleSelectDropdownAlignLeft]: alignment === 'left',
              [styles.SingleSelectDropdownAlignCenter]: alignment === 'center',
              [styles.SingleSelectDropdownAlignRight]: alignment === 'right',
              [styles.SingleSelectDropdownDirectionTop]: direction === 'top',
              [styles.SingleSelectDropdownDirectionBottom]:
                direction === 'bottom',
            })}
          >
            {isSearchable ? (
              <div className={styles.SingleSelectDropdownSearch}>
                <Input
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  className={styles.SingleSelectDropdownSearchInput}
                  placeholder={placeholder}
                  startIcon="search"
                  startIconSize="small"
                  endIconSize="xsmall"
                  endIcon={search ? 'cancel' : undefined}
                  onEndIconClick={() => setSearch('')}
                />
              </div>
            ) : null}
            <ul
              className={cn(
                styles.SingleSelectDropdownOptions,
                dropdownClassName
              )}
            >
              {filteredOptions.map((option) => {
                const { id, label } = option;
                const checked = value === option.value;
                return (
                  <li
                    key={id}
                    className={cn(styles.SingleSelectDropdownOption, {
                      [styles.SingleSelectDropdownOptionSelected]: checked,
                    })}
                    onClick={() => onSelect(option.value)}
                  >
                    <Radio
                      checked={checked}
                      readOnly
                      className={styles.SingleSelectDropdownOptionRadio}
                    >
                      {label}
                    </Radio>
                  </li>
                );
              })}
            </ul>
          </div>
        ) : null}
      </div>
    );
  }
);

SingleSelect.displayName = 'SingleSelect';
